import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChartPieIcon,
  ChartBarIcon, 
  BanknotesIcon, 
  ShieldCheckIcon, 
  BuildingOffice2Icon, 
  ScaleIcon,
  UserIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  CogIcon,
  UsersIcon,
  DocumentTextIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreServices = [
    {
      icon: ChartPieIcon,
      title: "Mutual Funds",
      description: "Diversified investment solutions with professional fund management across equity, fixed income, and alternative asset classes.",
      link: "/services/mutual-funds",
      features: ["Equity Funds", "Balanced Funds", "Fixed Income", "Sector Specific"]
    },
    {
      icon: ChartBarIcon,
      title: "Investment Management",
      description: "Comprehensive investment management services tailored to your financial goals with expert portfolio construction.",
      link: "/services/investment-management",
      features: ["Portfolio Construction", "Risk Management", "Performance Monitoring", "Strategic Allocation"]
    },
    {
      icon: BanknotesIcon,
      title: "Personal Retirement Account",
      description: "Tax-advantaged retirement planning with various IRA options to secure your financial future.",
      link: "/services/personal-retirement-account",
      features: ["Traditional IRA", "Roth IRA", "SEP-IRA", "Self-Directed IRA"]
    },
    {
      icon: BuildingOffice2Icon,
      title: "Securities Trading & Brokerage",
      description: "Full-service brokerage platform with advanced trading tools and research for active investors.",
      link: "/services/securities-trading-brokerage",
      features: ["Online Trading", "Research Tools", "Market Analysis", "Execution Services"]
    },
    {
      icon: ShieldCheckIcon,
      title: "Trustee & Registrar Services",
      description: "Professional trustee services and share registration for institutional clients and fund management.",
      link: "/services/trustee-registrar-services",
      features: ["Trust Administration", "Share Registration", "Custody Services", "Compliance Monitoring"]
    },
    {
      icon: ScaleIcon,
      title: "Wealth Management",
      description: "Holistic wealth management solutions combining investment strategy with comprehensive financial planning.",
      link: "/services/wealth-management",
      features: ["Financial Planning", "Tax Strategy", "Estate Planning", "Risk Assessment"]
    }
  ];

  const additionalServices = [
    {
      icon: UserIcon,
      title: "Financial Advising",
      description: "One-on-one financial advisory services to help you make informed investment decisions.",
      link: "/services/financial-advising"
    },
    {
      icon: BriefcaseIcon,
      title: "Portfolio Management",
      description: "Active portfolio management with regular rebalancing and performance optimization.",
      link: "/services/portfolio-management"
    },
    {
      icon: AcademicCapIcon,
      title: "Financial Planning",
      description: "Comprehensive financial planning to achieve your short and long-term financial goals.",
      link: "/services/financial-planning"
    },
    {
      icon: CogIcon,
      title: "Retirement Planning",
      description: "Strategic retirement planning to ensure financial security in your golden years.",
      link: "/services/retirement-planning"
    },
    {
      icon: UsersIcon,
      title: "Managed Portfolios",
      description: "Professionally managed portfolios designed for different risk profiles and time horizons.",
      link: "/services/managed-portfolios"
    },
    {
      icon: DocumentTextIcon,
      title: "Wealth & Estate Planning",
      description: "Comprehensive estate planning strategies to preserve and transfer wealth efficiently.",
      link: "/services/wealth-estate-planning"
    }
  ];

  const stats = [
    { value: "$11B+", label: "Assets Under Management" },
    { value: "15+", label: "Years of Experience" },
    { value: "700+", label: "Satisfied Clients" },
    { value: "24/7", label: "Client Support" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white pt-20 lg:pt-24 pb-16 lg:pb-24 overflow-hidden">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-center max-w-5xl mx-auto"
          >
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 font-heading"
            >
              Investment <span className="text-primary">Services</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl lg:text-2xl text-gray-600 leading-relaxed mb-12"
            >
              Comprehensive financial solutions designed to grow and protect your wealth through every stage of life.
            </motion.p>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-gray-600 text-sm lg:text-base">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16 lg:mb-20"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Core Services
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our flagship investment services designed to meet your most important financial objectives.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.1,
                    ease: [0.25, 0.46, 0.45, 0.94] 
                  }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <Link 
                    to={service.link}
                    className="block bg-gray-50 rounded-3xl p-8 hover:bg-white hover:shadow-xl transition-all duration-500 border border-gray-100 h-full"
                  >
                    <div className="mb-6">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center group-hover:bg-primary transition-colors duration-500">
                        <IconComponent className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-500" />
                      </div>
                    </div>
                    
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 font-heading">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    <div className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center text-primary group-hover:text-secondary transition-colors">
                      <span className="font-semibold">Learn More</span>
                      <ArrowRightIcon className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16 lg:mb-20"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Additional Services
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Specialized financial services to complement your investment strategy and enhance your financial well-being.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.1,
                    ease: [0.25, 0.46, 0.45, 0.94] 
                  }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <Link 
                    to={service.link}
                    className="block bg-white rounded-3xl p-8 hover:shadow-xl transition-all duration-500 border border-gray-100 h-full"
                  >
                    <div className="mb-6">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-primary transition-colors duration-500">
                        <IconComponent className="w-6 h-6 text-gray-600 group-hover:text-white transition-colors duration-500" />
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-4 font-heading">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    <div className="flex items-center text-gray-400 group-hover:text-primary transition-colors">
                      <span className="font-semibold">Explore</span>
                      <ArrowRightIcon className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary to-gray-900 text-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 font-heading">
              Ready to Get Started?
            </h2>
            <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed">
              Let our experienced team help you choose the right investment services for your financial goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-secondary text-primary px-8 py-4 rounded-2xl font-bold text-lg hover:bg-yellow-500 transition-colors inline-flex items-center gap-3"
              >
                Schedule Consultation
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-2xl font-bold text-lg transition-colors"
              >
                Contact Us
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;